// offline street routing: road network of the project area loaded once (OpenStreetMap / Overpass), stored on the device
import { get, set } from 'idb-keyval';
import { geoDist } from './geo';

const KEY = (pid) => `giss_roads_v1_${pid || 'x'}`;
const OVERPASS = ['https://overpass-api.de/api/interpreter', 'https://overpass.kumi.systems/api/interpreter', 'https://overpass.private.coffee/api/interpreter', 'https://maps.mail.ru/osm/tools/overpass/api/interpreter'];
const NO_CAR = new Set(['footway', 'path', 'steps', 'pedestrian', 'cycleway', 'bridleway', 'corridor', 'elevator', 'platform']);
const NO_FOOT = new Set(['motorway', 'motorway_link', 'trunk', 'trunk_link']);
const mem = {};
const graph = async (pid) => (mem[pid] ||= (await get(KEY(pid))) || null);

export const roadsMeta = async (pid) => { const g = await graph(pid); return g ? { bbox: g.bbox, km: g.km, at: g.at, ways: g.ways.length } : null; };
export const covers = (meta, pts) => !!meta && pts.every(([la, lo]) => la >= meta.bbox[0] && la <= meta.bbox[2] && lo >= meta.bbox[1] && lo <= meta.bbox[3]);

export const projPts = (p) => [...(p.fittings || []).map((f) => [f.lat, f.lon]), [p.lat, p.lon], ...(p.reports || []).map((r) => [r.lat, r.lon])].filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b));

// bbox [s, w, n, e] around points with a margin (m), capped at about 20 x 20 km
export function areaOf(pts, margin = 800) {
  const la = pts.map((p) => p[0]), lo = pts.map((p) => p[1]), c = Math.cos((la[0] * Math.PI) / 180);
  let s = Math.min(...la), n = Math.max(...la), w = Math.min(...lo), e = Math.max(...lo);
  const dLa = margin / 111320, dLo = margin / (111320 * c);
  s -= dLa; n += dLa; w -= dLo; e += dLo;
  const mx = 20000 / 111320, cy = (s + n) / 2, cx = (w + e) / 2;
  if (n - s > mx) { s = cy - mx / 2; n = cy + mx / 2; }
  if (e - w > mx / c) { w = cx - mx / c / 2; e = cx + mx / c / 2; }
  return [s, w, n, e];
}

export async function loadRoads(pid, bbox) {
  const q = `[out:json][timeout:90];way["highway"]["highway"!~"^(construction|proposed|abandoned|razed|disused)$"](${bbox.map((v) => v.toFixed(5)).join(',')});out geom tags;`;
  let js = null;
  for (const ep of OVERPASS) {
    try {
      const ctl = new AbortController(), id = setTimeout(() => ctl.abort(), 35000);
      const r = await fetch(ep, { method: 'POST', body: `data=${encodeURIComponent(q)}`, headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, signal: ctl.signal });
      clearTimeout(id);
      if (r.ok) { js = await r.json(); if (js?.elements) break; }
    } catch { /* next mirror */ }
  }
  if (!js?.elements) throw new Error('Overpass');
  const idx = new Map(), lat = [], lon = [], ways = [];
  let km = 0;
  (js.elements || []).filter((w) => w.type === 'way' && w.nodes?.length > 1 && w.geometry?.length === w.nodes.length).forEach((w) => {
    const n = w.nodes.map((id, i) => { if (!idx.has(id)) { idx.set(id, lat.length); lat.push(+w.geometry[i].lat.toFixed(6)); lon.push(+w.geometry[i].lon.toFixed(6)); } return idx.get(id); });
    const ow = w.tags?.oneway === '-1' ? -1 : ['yes', '1', 'true'].includes(w.tags?.oneway) || w.tags?.junction === 'roundabout' ? 1 : 0;
    ways.push({ n, hw: w.tags?.highway || '', nm: w.tags?.name || w.tags?.ref || '', ow });
    for (let i = 1; i < n.length; i++) km += geoDist([lat[n[i - 1]], lon[n[i - 1]]], [lat[n[i]], lon[n[i]]]) / 1000;
  });
  if (!ways.length) throw new Error('empty');
  mem[pid] = { bbox, lat, lon, ways, km: Math.round(km), at: Date.now() };
  await set(KEY(pid), mem[pid]);
  window.dispatchEvent(new CustomEvent('giss-roads-saved', { detail: pid }));
  return roadsMeta(pid);
}

// adjacency per travel mode (built lazily from the stored ways)
const adjCache = {};
function adjOf(g, mode) {
  const k = `${g.at}:${mode}`;
  if (adjCache[k]) return adjCache[k];
  const adj = Array.from({ length: g.lat.length }, () => []);
  g.ways.forEach((w, wi) => {
    if (mode === 'car' ? NO_CAR.has(w.hw) : NO_FOOT.has(w.hw)) return;
    for (let i = 1; i < w.n.length; i++) {
      const a = w.n[i - 1], b = w.n[i], d = geoDist([g.lat[a], g.lon[a]], [g.lat[b], g.lon[b]]);
      const car = mode === 'car' ? w.ow : 0;
      if (car >= 0) adj[a].push([b, d, wi]);
      if (car <= 0) adj[b].push([a, d, wi]);
    }
  });
  return (adjCache[k] = adj);
}

function nearest(g, adj, p) {
  let bi = -1, bd = Infinity;
  for (let i = 0; i < g.lat.length; i++) { if (!adj[i].length) continue; const d = (g.lat[i] - p[0]) ** 2 + ((g.lon[i] - p[1]) * Math.cos((p[0] * Math.PI) / 180)) ** 2; if (d < bd) { bd = d; bi = i; } }
  return bi;
}

// A* on the street graph; returns route points, length and turn steps (OSRM-like for the navigation banner)
export async function routeOff(pid, a, b, mode = 'foot') {
  const g = await graph(pid);
  if (!g) return null;
  const adj = adjOf(g, mode), s = nearest(g, adj, a), t = nearest(g, adj, b);
  if (s < 0 || t < 0) return null;
  const P = (i) => [g.lat[i], g.lon[i]], h = (i) => geoDist(P(i), P(t));
  const dist = new Float64Array(g.lat.length).fill(Infinity), prev = new Int32Array(g.lat.length).fill(-1), pw = new Int32Array(g.lat.length).fill(-1);
  const heap = [[h(s), s]], push = (x) => { heap.push(x); let i = heap.length - 1; while (i > 0) { const p = (i - 1) >> 1; if (heap[p][0] <= heap[i][0]) break; [heap[p], heap[i]] = [heap[i], heap[p]]; i = p; } };
  const pop = () => { const top = heap[0], last = heap.pop(); if (heap.length) { heap[0] = last; let i = 0; for (;;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < heap.length && heap[l][0] < heap[m][0]) m = l; if (r < heap.length && heap[r][0] < heap[m][0]) m = r; if (m === i) break; [heap[m], heap[i]] = [heap[i], heap[m]]; i = m; } } return top; };
  dist[s] = 0;
  while (heap.length) {
    const [, u] = pop();
    if (u === t) break;
    for (const [v, d, wi] of adj[u]) { const nd = dist[u] + d; if (nd < dist[v]) { dist[v] = nd; prev[v] = u; pw[v] = wi; push([nd + h(v), v]); } }
  }
  if (!Number.isFinite(dist[t])) return null;
  const ids = []; for (let i = t; i >= 0; i = prev[i]) ids.unshift(i);
  const pts = [a, ...ids.map(P), b];
  return { pts, steps: turnSteps(g, ids, pw, b, adj), m: dist[t] + geoDist(a, P(s)) + geoDist(P(t), b), off: true };
}

const brg = (p, q) => { const r = Math.PI / 180, y = Math.sin((q[1] - p[1]) * r) * Math.cos(q[0] * r), x = Math.cos(p[0] * r) * Math.sin(q[0] * r) - Math.sin(p[0] * r) * Math.cos(q[0] * r) * Math.cos((q[1] - p[1]) * r); return (Math.atan2(y, x) / r + 360) % 360; };
function turnSteps(g, ids, pw, b, adj) {
  const P = (i) => [g.lat[i], g.lon[i]], out = [];
  for (let k = 1; k < ids.length - 1; k++) {
    const w0 = g.ways[pw[ids[k]]], w1 = g.ways[pw[ids[k + 1]]];
    const d = ((brg(P(ids[k]), P(ids[k + 1])) - brg(P(ids[k - 1]), P(ids[k])) + 540) % 360) - 180, ad = Math.abs(d);
    if (w0?.nm === w1?.nm && (ad < 30 || adj[ids[k]].length <= 2)) continue;
    if (ad < 20) continue;
    const side = d < 0 ? 'left' : 'right', mod = ad < 45 ? `slight ${side}` : ad < 135 ? side : ad < 165 ? `sharp ${side}` : 'uturn';
    out.push({ lat: g.lat[ids[k]], lon: g.lon[ids[k]], type: 'turn', mod, name: w1?.nm || '' });
  }
  out.push({ lat: b[0], lon: b[1], type: 'arrive', mod: '' });
  return out;
}
